/**
 * Bottom navigation component
 *
 * Mobile-only bottom navigation bar with icons.
 * Hidden on desktop, visible on mobile.
 * AI Check-in is positioned centrally as the primary action.
 *
 * V1-inspired: frosted glass background, refined active indicators
 * with teal accent glow, clean icon styling.
 */
import { Link, useLocation } from "@tanstack/react-router";
import { Loader2, Mic, Square } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import {
  BOTTOM_NAV_ITEMS,
  NAV_PATHS,
  isActive,
  isHighlightedItem,
  isCenterItem,
} from "./navigation-utils";
import {
  AI_CHECKIN_MIC_STATUS_EVENT,
  dispatchAiCheckInToggleMic,
  type AiCheckInMicStatus,
} from "@/lib/ai-check-in-events";
import {
  CENTER_ACTION_OVERRIDE_EVENT,
  dispatchCenterActionTap,
  type CenterActionOverride,
} from "@/lib/center-action-events";

/**
 * Bottom navigation bar for mobile
 *
 * Frosted glass surface with:
 * - Active state: teal accent color with scale + bold label
 * - Center item (AI Check-in): elevated FAB with glow
 * - Smooth transitions on all state changes
 */
export function BottomNav() {
  const location = useLocation();
  const { t } = useTranslation("common");

  const isAiCheckInRoute = isActive(location.pathname, NAV_PATHS.AI_CHECK_IN);
  const [micStatus, setMicStatus] = useState<AiCheckInMicStatus>({
    isRecording: false,
    isTranscribing: false,
    isAvailable: true,
  });

  // Generic center-action override (from dialogs that temporarily take over
  // the center FAB, e.g. doctor-visit voice input).
  const [centerOverride, setCenterOverride] = useState<CenterActionOverride | null>(null);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const handleStatus = (event: Event) => {
      const detail = (event as CustomEvent<AiCheckInMicStatus>).detail;
      if (!detail) return;
      setMicStatus(detail);
    };

    window.addEventListener(AI_CHECKIN_MIC_STATUS_EVENT, handleStatus);
    return () => window.removeEventListener(AI_CHECKIN_MIC_STATUS_EVENT, handleStatus);
  }, []);

  // Listen for generic center-action override events.
  useEffect(() => {
    if (typeof window === "undefined") return;

    const handleOverride = (event: Event) => {
      const detail = (event as CustomEvent<CenterActionOverride>).detail;
      if (!detail) return;
      // icon === null means "clear the override"
      setCenterOverride(detail.icon === null ? null : detail);
    };

    window.addEventListener(CENTER_ACTION_OVERRIDE_EVENT, handleOverride);
    return () =>
      window.removeEventListener(CENTER_ACTION_OVERRIDE_EVENT, handleOverride);
  }, []);

  useEffect(() => {
    if (!isAiCheckInRoute) {
      setMicStatus({ isRecording: false, isTranscribing: false, isAvailable: true });
    }
  }, [isAiCheckInRoute]);

  const centerIcon = useMemo(() => {
    if (!isAiCheckInRoute) return null;
    if (micStatus.isTranscribing) return Loader2;
    if (micStatus.isRecording) return Square;
    return Mic;
  }, [isAiCheckInRoute, micStatus.isRecording, micStatus.isTranscribing]);

  // Resolve the icon for a generic override.
  const overrideIcon = useMemo(() => {
    if (!centerOverride) return null;
    if (centerOverride.icon === "loading") return Loader2;
    if (centerOverride.icon === "stop") return Square;
    return Mic;
  }, [centerOverride]);

  // Determine if the generic override should take effect. It wins over the
  // default AI-check-in hero mic when we are NOT on the AI-check-in route.
  const hasGenericOverride = centerOverride !== null && !isAiCheckInRoute;

  return (
    <nav
      aria-label={t("nav.mobileNavigation")}
      className={cn(
        "fixed bottom-0 left-0 right-0 z-40 lg:hidden",
        "h-16 border-t border-border/50",
        "bg-background/85 backdrop-blur-xl",
        "supports-[backdrop-filter]:bg-background/70"
      )}
    >
      <div className="flex h-full items-center justify-around">
        {BOTTOM_NAV_ITEMS.map((item, index) => {
          const active = isActive(location.pathname, item.path);
          const highlighted = isHighlightedItem(item);
          const isCenter = isCenterItem(BOTTOM_NAV_ITEMS, index);
          const Icon = item.icon;

          // When a dialog temporarily owns the center action (e.g. voice input),
          // collapse mobile nav to a mic-only control to reduce clutter.
          if (hasGenericOverride && !(isCenter && highlighted)) {
            return null;
          }

          // Center item (AI Check-in) gets elevated FAB styling with glow
          if (isCenter && highlighted) {
            // -- Generic override (dialog-driven, e.g. doctor-visit voice) --
            if (hasGenericOverride && overrideIcon) {
              const OverrideIcon = overrideIcon;
              const isOvRecording = !!centerOverride?.isRecording;
              const isOvDisabled = !!centerOverride?.disabled;

              const ovFab = (
                <div
                  className={cn(
                    "relative flex h-12 w-12 items-center justify-center rounded-full",
                    "shadow-lg transition-all duration-200 hover:scale-105 hover:shadow-xl",
                    "active:scale-95",
                    isOvRecording
                      ? "bg-red-500 text-white shadow-red-500/25 hover:shadow-red-500/30"
                      : "bg-primary text-primary-foreground shadow-primary/25 hover:shadow-primary/30",
                  )}
                >
                  {isOvRecording && (
                    <div className="pointer-events-none absolute inset-0" aria-hidden="true">
                      {[0, 1, 2].map((idx) => (
                        <div
                          key={`bottomnav-ov-ring-${idx}`}
                          className="absolute inset-0 rounded-full border-2 border-red-400/40"
                          style={{
                            animationName: "bottomnav-mic-pulse",
                            animationDuration: "1.35s",
                            animationTimingFunction: "ease-out",
                            animationIterationCount: "infinite",
                            animationDelay: `${idx * 0.32}s`,
                          }}
                        />
                      ))}
                    </div>
                  )}
                  <OverrideIcon
                    className={cn(
                      "h-6 w-6",
                      centerOverride?.icon === "loading" && "animate-spin",
                    )}
                  />
                </div>
              );

              const ovLabel = (
                <span className="text-xs font-medium tracking-tight text-muted-foreground">
                  {isOvRecording
                    ? t("nav.stopShort")
                    : centerOverride?.icon === "loading"
                      ? t("nav.transcribingShort")
                      : t("nav.recordShort")}
                </span>
              );

              return (
                <button
                  key={item.path}
                  type="button"
                  onClick={dispatchCenterActionTap}
                  disabled={isOvDisabled}
                  className={cn(
                    "flex flex-1 flex-col items-center justify-center gap-1 py-2 -mt-4 transition-all duration-200",
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                    isOvDisabled && "cursor-not-allowed opacity-60",
                  )}
                  aria-label={centerOverride?.ariaLabel ?? "Toggle recording"}
                >
                  <style>{`
                    @keyframes bottomnav-mic-pulse {
                      0% { transform: scale(1); opacity: 0.6; }
                      100% { transform: scale(1.8); opacity: 0; }
                    }
                  `}</style>
                  {ovFab}
                  {ovLabel}
                </button>
              );
            }

            // -- Default: AI Check-in hero mic / link --
            const CenterIcon = isAiCheckInRoute ? centerIcon : null;
            const showHeroMic = isAiCheckInRoute && CenterIcon !== null;

            const FabInner = (
              <div
                className={cn(
                  "relative flex h-12 w-12 items-center justify-center rounded-full",
                  "shadow-lg transition-all duration-200 hover:scale-105 hover:shadow-xl",
                  "active:scale-95",
                  showHeroMic
                    ? micStatus.isRecording
                      ? "bg-red-500 text-white shadow-red-500/25 hover:shadow-red-500/30"
                      : "bg-primary text-primary-foreground shadow-primary/25 hover:shadow-primary/30"
                    : "bg-primary text-primary-foreground shadow-primary/25 hover:shadow-primary/30",
                  active && "ring-2 ring-primary/30 ring-offset-2 ring-offset-background"
                )}
              >
                {showHeroMic && (
                  <div className="pointer-events-none absolute inset-0" aria-hidden="true">
                    {[0, 1, 2].map((idx) => (
                      <div
                        key={`bottomnav-mic-ring-${idx}`}
                        className={cn(
                          "absolute inset-0 rounded-full border-2",
                          micStatus.isRecording
                            ? "border-red-400/40"
                            : "border-primary/25"
                        )}
                        style={{
                          animationName: micStatus.isRecording
                            ? "bottomnav-mic-pulse"
                            : "bottomnav-mic-breathe",
                          animationDuration: micStatus.isRecording ? "1.35s" : "3.6s",
                          animationTimingFunction: micStatus.isRecording ? "ease-out" : "ease-in-out",
                          animationIterationCount: "infinite",
                          animationDelay: `${idx * 0.32}s`,
                        }}
                      />
                    ))}
                  </div>
                )}

                {showHeroMic ? (
                  <CenterIcon
                    className={cn(
                      "h-6 w-6",
                      micStatus.isTranscribing && "animate-spin"
                    )}
                  />
                ) : (
                  <Icon className="h-6 w-6" />
                )}
              </div>
            );

            const baseClasses = cn(
              "flex flex-1 flex-col items-center justify-center gap-1 py-2",
              "-mt-4",
              "transition-all duration-200"
            );

            const displayLabelKey =
              isAiCheckInRoute &&
              showHeroMic &&
              !micStatus.isRecording &&
              !micStatus.isTranscribing
                ? "nav.recordShort"
                : item.labelKey;

            const label = (
              <span
                className={cn(
                  "text-xs font-medium tracking-tight",
                  active ? "text-primary" : "text-muted-foreground"
                )}
              >
                {t(displayLabelKey)}
              </span>
            );

            if (isAiCheckInRoute) {
              return (
                <button
                  key={item.path}
                  type="button"
                  onClick={dispatchAiCheckInToggleMic}
                  disabled={!micStatus.isRecording && !micStatus.isAvailable}
                  className={cn(
                    baseClasses,
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                    (!micStatus.isRecording && !micStatus.isAvailable) &&
                      "cursor-not-allowed opacity-60"
                  )}
                  aria-label={
                    micStatus.isRecording
                      ? "Stop recording"
                      : micStatus.isTranscribing
                        ? "Transcribing"
                        : "Start recording"
                  }
                >
                  <style>{`
                    @keyframes bottomnav-mic-pulse {
                      0% { transform: scale(1); opacity: 0.6; }
                      100% { transform: scale(1.8); opacity: 0; }
                    }
                    @keyframes bottomnav-mic-breathe {
                      0%, 100% { transform: scale(1); opacity: 0.2; }
                      50% { transform: scale(1.12); opacity: 0.45; }
                    }
                  `}</style>
                  {FabInner}
                  {label}
                </button>
              );
            }

            return (
              <Link key={item.path} to={item.path} hash="" className={baseClasses}>
                {FabInner}
                {label}
              </Link>
            );
          }

          // Regular navigation items -- clean active indicator via color + scale
          return (
            <Link
              key={item.path}
              to={item.path}
              hash=""
              className={cn(
                "group flex flex-1 flex-col items-center justify-center gap-1 py-2",
                "transition-all duration-200",
                active
                  ? "text-primary"
                  : "text-muted-foreground active:text-foreground"
              )}
            >
              <Icon
                className={cn(
                  "h-5 w-5 transition-transform duration-200",
                  active && "scale-110"
                )}
              />
              <span
                className={cn(
                  "text-xs font-medium tracking-tight",
                  active && "font-semibold"
                )}
              >
                {t(item.labelKey)}
              </span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
}
